import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { billingService } from '../../services/billingService';
import {
  activeSubscription, describeSubscription, daysLeft, expiryTone,
  FREE_FALLBACK, toDateInput, endOfDayIso, dateInputPlusDays,
} from '../../lib/subscription';

const PERIODS = [
  { key: 'month', label: 'месяц' },
  { key: 'year', label: 'год' },
];

const TONE_STYLES = {
  expired: 'text-paper-accent',
  soon: 'text-paper-warning',
  ok: 'text-paper-ink-3',
};

/**
 * Тариф клиента в его карточке: что действует сейчас и выдача нового селектом.
 *
 * Выдача бьёт в bit-billing сразу по кнопке, а не по «Сохранить» диалога:
 * остальные поля карточки — это один PATCH в bit-auth, подписка живёт в другом
 * сервисе, и складывать их в одно действие значило бы врать про атомарность.
 */
export default function SubscriptionPanel({ email }) {
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planCode, setPlanCode] = useState('');
  const [period, setPeriod] = useState('month');
  const [until, setUntil] = useState('');   // пусто = полный период тарифа
  // Реквизиты организации, которая заводится вместе с платным тарифом. Без них
  // она получала имя домена — «yandex.ru» вместо «ООО Проверка» (прогон 24.09, Б3).
  const [companyName, setCompanyName] = useState('');
  const [inn, setInn] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      const [plansData, subsData, lastInvoice] = await Promise.all([
        billingService.getPlans(),
        billingService.adminGetSubscriptions({ email, page_size: 20 }),
        // Подсказка, а не условие: заявки может не быть, и тогда поля пустые.
        billingService.adminGetInvoiceRequests({ email, page_size: 1 }).catch(() => null),
      ]);
      const last = lastInvoice?.items?.[0];
      if (last) {
        setCompanyName((v) => v || last.company_name || '');
        setInn((v) => v || last.inn || '');
      }
      // free из списка выдачи убран: клиент без подписки и так считается по Free,
      // выдавать его отдельной подпиской нечего.
      const issuable = (plansData.plans || []).filter((p) => p.code !== 'free');
      setPlans(issuable);
      setSub(activeSubscription(subsData.items));
      setPlanCode((prev) => prev || issuable[0]?.code || '');
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось загрузить тарифы');
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => { load(); }, [load]);

  const planTitle = plans.find((p) => p.code === sub?.plan_code)?.title;
  const left = sub ? daysLeft(sub.paid_until) : null;
  const tone = sub ? expiryTone(sub.paid_until) : 'ok';

  const handleIssue = async () => {
    if (!planCode) return;
    setSaving(true);
    setError(null);
    setDone(null);
    try {
      const created = await billingService.adminCreateSubscription({
        email, plan_code: planCode, billing_period: period,
        company_name: companyName.trim() || null,
        inn: inn.trim() || null,
      });
      // Свой срок — вторым запросом: POST считает paid_until по периоду тарифа,
      // а демо-выдача короче месяца задаётся только сдвигом paid_until.
      if (until) {
        try {
          await billingService.adminUpdateSubscription(created.id, { paid_until: endOfDayIso(until) });
        } catch (e) {
          // Подписка уже создана — на полный период. Сказать «не удалось выдать»
          // было бы враньём: админ нажал бы ещё раз, тот же план ушёл бы в
          // extend и добавил лишний месяц. Честно: тариф есть, срок не тот.
          setError('Тариф выдан, но срок остался полным периодом — поправьте срок'
            + ` (${e.response?.data?.detail || 'вторая часть запроса не прошла'})`);
          await load();
          return;
        }
      }
      setDone('Тариф выдан');
      setUntil('');
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось выдать тариф');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-chip bg-paper-bg p-3 space-y-3">
      <div className="text-xs text-paper-ink-3">Тариф</div>

      {loading ? (
        <div className="text-sm text-paper-hint">Загрузка…</div>
      ) : sub ? (
        <div className="text-sm">
          <div className="text-paper-ink">{describeSubscription(sub, planTitle)}</div>
          <div className={`text-xs mt-0.5 ${TONE_STYLES[tone]}`}>
            {left > 0 ? `осталось ${left} дн.` : 'срок истёк — действует «Оплата по факту»'}
            {' · '}{sub.minutes_limit} мин, {sub.llm_limit} саммари
          </div>
        </div>
      ) : (
        <div className="text-sm text-paper-ink-3">
          Тарифа нет — действует «Оплата по факту»
          {' '}({FREE_FALLBACK.minutes} мин, {FREE_FALLBACK.summaries} саммари)
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2">
          <label htmlFor="sub-plan" className="text-[11px] text-paper-hint block mb-1">Выдать тариф</label>
          <select
            id="sub-plan"
            className="w-full h-9 rounded-chip bg-paper-bg px-2 text-sm"
            value={planCode}
            onChange={(e) => setPlanCode(e.target.value)}
          >
            {plans.map((p) => (
              <option key={p.code} value={p.code}>
                {p.title} — {p.minutes_per_period} мин, {p.llm_summaries_per_period} саммари
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="sub-period" className="text-[11px] text-paper-hint block mb-1">Период</label>
          <select
            id="sub-period"
            className="w-full h-9 rounded-chip bg-paper-bg px-2 text-sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2">
          <label htmlFor="sub-company" className="text-[11px] text-paper-hint block mb-1">
            Название организации (пусто — по домену почты)
          </label>
          <Input id="sub-company" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                 placeholder="ООО «Ромашка»" />
        </div>
        <div>
          <label htmlFor="sub-inn" className="text-[11px] text-paper-hint block mb-1">ИНН</label>
          <Input id="sub-inn" value={inn} onChange={(e) => setInn(e.target.value)} inputMode="numeric" />
        </div>
      </div>

      <div>
        <label htmlFor="sub-until" className="text-[11px] text-paper-hint block mb-1">
          Свой срок (для демо; пусто — весь период тарифа)
        </label>
        <div className="flex gap-2">
          <Input id="sub-until" type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
          <Button variant="outline" size="sm" type="button" onClick={() => setUntil(dateInputPlusDays(14))}>
            +14 дн.
          </Button>
        </div>
      </div>

      {error && <div className="bg-paper-accent/[0.06] text-paper-accent rounded p-2 text-xs">{error}</div>}
      {done && <div className="bg-paper-success/[0.08] text-paper-success rounded-chip p-2 text-xs">{done}</div>}

      <Button
        size="sm"
        className="bg-paper-accent hover:bg-paper-accent-hover w-full"
        type="button"
        onClick={handleIssue}
        disabled={saving || loading || !planCode}
      >
        {saving ? 'Выдача…' : (sub ? 'Заменить тариф' : 'Выдать тариф')}
      </Button>
      <p className="text-[11px] text-paper-hint">
        Выдача применяется сразу, отдельно от кнопки «Сохранить»
        {sub ? '. Тот же тариф продлевает срок, другой — заменяет текущий.' : '.'}
        {' '}Истечение срока никем не отслеживается: клиент молча упадёт на «Оплату по факту».
      </p>
      {sub && (
        <p className="text-[11px] text-paper-hint">
          Текущий срок:{' '}
          <span className="font-mono">{toDateInput(sub.paid_until)}</span>
        </p>
      )}
    </div>
  );
}
